// src/pages/PromoScanPage.jsx
// Standalone page — accessible only via QR scan from the pay station.
// URL: /promo-scan?code=XXXX&plate=ABC123
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  EnvelopeIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { API_BASE } from "../config";

// ─── Animations ───────────────────────────────────────────────────
const pageVariants = {
  hidden: { opacity: 0, y: 22 },
  show:   { opacity: 1, y: 0,  transition: { duration: 0.42, ease: "easeOut" } },
  exit:   { opacity: 0, y: -18, transition: { duration: 0.25, ease: "easeIn" } },
};
const stagger = {
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show:   { opacity: 1, y: 0,  transition: { duration: 0.35, ease: "easeOut" } },
};

// ─── Car icon SVG ─────────────────────────────────────────────────
const CarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
      d="M8 17H16M5 17H4a1 1 0 01-1-1v-4l2-5h14l2 5v4a1 1 0 01-1 1h-1m-9 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  </svg>
);

// ─── Spinner ──────────────────────────────────────────────────────
const Spinner = () => (
  <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

// ─── Skeleton shown during API call ───────────────────────────────
const ResultSkeleton = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    className="animate-pulse flex flex-col gap-5 w-full"
  >
    {/* Card skeleton */}
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-100" />
          <div>
            <div className="h-3.5 bg-gray-100 rounded w-24 mb-1.5" />
            <div className="h-2.5 bg-gray-100 rounded w-32" />
          </div>
        </div>
        <div className="h-5 bg-gray-100 rounded-full w-24" />
      </div>
      {/* Reward row */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="h-2.5 bg-gray-100 rounded w-20 mb-2" />
        <div className="h-5 bg-gray-100 rounded w-48" />
      </div>
      {/* Code block */}
      <div className="px-5 py-5">
        <div className="h-2.5 bg-gray-100 rounded w-20 mb-3" />
        <div className="h-14 bg-gray-100 rounded-xl w-full" />
        <div className="h-2.5 bg-gray-100 rounded w-36 mx-auto mt-3" />
      </div>
      {/* QR placeholder */}
      <div className="px-5 pb-7 flex flex-col items-center">
        <div className="w-full border-t border-gray-100 mb-5" />
        <div className="h-2.5 bg-gray-100 rounded w-40 mb-5" />
        <div className="w-[220px] h-[220px] bg-gray-100 rounded-xl" />
        <div className="h-2.5 bg-gray-100 rounded w-52 mt-5" />
      </div>
    </div>
    {/* Instructions skeleton */}
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
      <div className="h-3 bg-gray-100 rounded w-28 mb-4" />
      {[64, 52, 72, 40].map((w, i) => (
        <div key={i} className="flex items-center gap-2.5 mb-3 last:mb-0">
          <div className="w-5 h-5 rounded-full bg-gray-100 flex-shrink-0" />
          <div className="h-2.5 bg-gray-100 rounded" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  </motion.div>
);

// ─── Form input wrapper ───────────────────────────────────────────
const Field = ({ label, icon, error, children }) => (
  <motion.div variants={fadeUp} className="flex flex-col gap-1.5">
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
      {label}
    </label>
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        {icon}
      </span>
      {children}
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="text-[11px] text-red-500 flex items-center gap-1"
        >
          <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </motion.div>
);

const inputCls = [
  "w-full pl-11 pr-4 py-3.5 text-[14px] bg-white border border-gray-200",
  "rounded-xl text-gray-800 placeholder-gray-400 outline-none",
  "focus:border-[#1a1f6c]/50 focus:ring-2 focus:ring-[#1a1f6c]/10 transition-all duration-200",
].join(" ");

// ─── Type badge ───────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const map = {
    promo:     { cls: "bg-orange-100 text-orange-700 border-orange-200", label: "🏷️ Promo Code" },
    punchcard: { cls: "bg-violet-100 text-violet-700 border-violet-200", label: "🥊 Punch Reward" },
    points:    { cls: "bg-blue-100 text-blue-700 border-blue-200",       label: "⭐ Points Reward" },
  };
  const { cls, label } = map[type] ?? { cls: "bg-gray-100 text-gray-600 border-gray-200", label: "🎁 Reward" };
  return (
    <span className={`text-[10px] font-bold px-3 py-1 rounded-full border whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
};

// ─── Main ─────────────────────────────────────────────────────────
export const PromoScanPage = () => {
  const [searchParams] = useSearchParams();
  // Accept both English (code/plate) and Spanish (Codigo/Placa) param names
  const codeFromUrl  = searchParams.get("code")  || searchParams.get("Codigo") || searchParams.get("codigo") || "";
  const plateFromUrl = (searchParams.get("plate") || searchParams.get("Placa")  || searchParams.get("placa")  || "").toUpperCase();

  const [plate,    setPlate]    = useState(plateFromUrl);
  const [email,    setEmail]    = useState("");
  const [errors,   setErrors]   = useState({});
  const [apiErr,   setApiErr]   = useState("");
  const [phase,    setPhase]    = useState("form");    // "form" | "loading" | "result"
  const [result,   setResult]   = useState(null);
  const [copied,   setCopied]   = useState(false);
  const [qrLoaded, setQrLoaded] = useState(false);

  // No valid code in URL → show invalid state
  const noCode = !codeFromUrl.trim();

  const validate = () => {
    const e = {};
    if (!plate.trim())           e.plate = "License plate is required.";
    if (!email.trim())           e.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setApiErr("");
    setPhase("loading");

    try {
      const res = await fetch(`${API_BASE}/loyalty/promo-scan/register`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code:  codeFromUrl.trim().toUpperCase(),
          plate: plate.trim().toUpperCase(),
          email: email.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Something went wrong.");
      setResult(data);
      setPhase("result");
    } catch (err) {
      setApiErr(err.message || "Connection error. Please try again.");
      setPhase("form");
    }
  };

  const copyCode = () => {
    const c = result?.code;
    if (!c) return;
    navigator.clipboard.writeText(c).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }).catch(() => {});
  };

  // QR encodes just the code value so the kiosk scanner can read it directly
  const qrSrc = result?.code
    ? `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(result.code)}&size=240x240&bgcolor=ffffff&color=1a1f6c&qzone=2&format=png`
    : null;

  // ── Invalid link ───────────────────────────────────────────────
  if (noCode) {
    return (
      <div className="min-h-screen bg-[#f4f5f9] flex flex-col">
        <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <img src="/kinitas-logo.svg" alt="Kinitas" className="h-7 w-auto" />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="bg-white border border-gray-100 rounded-2xl shadow-md px-8 py-12 max-w-sm w-full text-center"
          >
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <ExclamationCircleIcon className="w-7 h-7 text-red-400" />
            </div>
            <h2 className="text-[18px] font-bold text-gray-800 mb-2">Invalid Link</h2>
            <p className="text-[13px] text-gray-500 leading-relaxed">
              This link is not valid. Please scan the QR code shown on the pay station screen.
            </p>
            <img src="/kinitas-logo.svg" alt="Kinitas" className="h-5 w-auto mx-auto mt-8 opacity-20" />
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f5f9]">

      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <img src="/kinitas-logo.svg" alt="Kinitas" className="h-7 w-auto" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1a1f6c]/50">
            Reward Center
          </span>
        </div>
      </div>

      {/* ── Hero strip ── */}
      <div className="bg-[#1a1f6c] px-4 pt-10 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="max-w-lg mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 18 }}
            className="w-20 h-20 rounded-full bg-white/10 border border-white/15 flex items-center justify-center mx-auto mb-5"
          >
            <span className="text-[38px] leading-none select-none">
              {phase === "result" ? "🎉" : "🎁"}
            </span>
          </motion.div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-2">
            Kinitas Car Wash
          </p>
          <h1 className="text-[24px] sm:text-[28px] font-bold text-white leading-tight">
            {phase === "result" ? "Here's your reward!" : "You've earned a reward!"}
          </h1>
          <p className="text-[13px] text-white/50 mt-2 max-w-xs mx-auto leading-relaxed">
            {phase === "result"
              ? "Save the code or let the kiosk scan the QR from your screen."
              : "Enter your info below to unlock and view your code."}
          </p>
        </motion.div>
      </div>

      {/* ── Main content ── */}
      <div className="px-4 py-7 pb-12 max-w-lg mx-auto">
        <AnimatePresence mode="wait">

          {/* ── PHASE: Form ── */}
          {phase === "form" && (
            <motion.div
              key="form"
              variants={stagger}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, y: -16, transition: { duration: 0.22 } }}
              className="flex flex-col gap-5"
            >
              {/* API error */}
              <AnimatePresence>
                {apiErr && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-start gap-2.5 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-[12px] text-red-600"
                  >
                    <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {apiErr}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form card */}
              <motion.div variants={fadeUp} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#1a1f6c]/8 flex items-center justify-center flex-shrink-0">
                    <span className="text-[18px]">📋</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-gray-800">Claim Your Reward</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Takes less than 10 seconds</p>
                  </div>
                </div>

                <div className="px-5 py-6 flex flex-col gap-5">
                  <Field
                    label="License Plate *"
                    icon={<CarIcon />}
                    error={errors.plate}
                  >
                    <input
                      value={plate}
                      onChange={(e) => {
                        setPlate(e.target.value.toUpperCase());
                        setErrors((p) => ({ ...p, plate: "" }));
                      }}
                      placeholder="ABC1234"
                      className={`${inputCls} font-mono tracking-widest`}
                      maxLength={10}
                    />
                  </Field>

                  <Field
                    label="Email Address *"
                    icon={<EnvelopeIcon className="w-5 h-5" />}
                    error={errors.email}
                  >
                    <input
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((p) => ({ ...p, email: "" }));
                      }}
                      placeholder="you@example.com"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      className={inputCls}
                    />
                  </Field>

                  <motion.div
                    variants={fadeUp}
                    className="flex items-start gap-2.5 bg-[#1a1f6c]/4 border border-[#1a1f6c]/8 rounded-xl px-4 py-3"
                  >
                    <span className="text-[15px] flex-shrink-0 mt-0.5">🔒</span>
                    <p className="text-[10px] text-[#1a1f6c]/70 leading-relaxed">
                      We only use your email for reward notifications and receipts. No spam — ever.
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* CTA button */}
              <motion.button
                variants={fadeUp}
                onClick={handleSubmit}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl
                           text-[15px] font-bold bg-[#1a1f6c] text-white
                           shadow-lg shadow-[#1a1f6c]/25
                           hover:bg-[#2832c2] active:scale-[0.985] transition-all duration-200"
              >
                <span className="text-[18px]">🎁</span>
                Reveal My Reward
              </motion.button>

              <motion.p variants={fadeUp} className="text-center text-[10px] text-gray-400 -mt-2">
                Your code is tied to your vehicle plate and valid for one wash.
              </motion.p>
            </motion.div>
          )}

          {/* ── PHASE: Loading skeleton ── */}
          {phase === "loading" && (
            <ResultSkeleton key="loading" />
          )}

          {/* ── PHASE: Result ── */}
          {phase === "result" && result && (
            <motion.div
              key="result"
              variants={stagger}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, y: -16, transition: { duration: 0.22 } }}
              className="flex flex-col gap-5"
            >

              {/* ── Main reward card ── */}
              <motion.div
                variants={fadeUp}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-md"
              >
                {/* Card header */}
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.25, type: "spring", stiffness: 280, damping: 20 }}
                      className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0"
                    >
                      <CheckIcon className="w-5 h-5 text-emerald-500" />
                    </motion.div>
                    <div>
                      <p className="text-[14px] font-bold text-gray-800">Code Ready!</p>
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[180px]">
                        {result.plate || plate} · {email}
                      </p>
                    </div>
                  </div>
                  <TypeBadge type={result.type} />
                </div>

                {/* Reward label */}
                <div className="px-5 py-4 border-b border-gray-100 bg-[#1a1f6c]/[0.025]">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                    Your Reward
                  </p>
                  <p className="text-[17px] font-bold text-[#1a1f6c] leading-tight">
                    {result.reward}
                  </p>
                </div>

                {/* Code display — tappable to copy */}
                <div className="px-5 py-5 border-b border-gray-100">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">
                    Your Code
                  </p>
                  <button
                    onClick={copyCode}
                    className="w-full flex items-center justify-between gap-3
                               bg-gray-50 border border-gray-200 rounded-xl px-5 py-4
                               group hover:border-[#1a1f6c]/30 hover:bg-[#1a1f6c]/[0.02]
                               transition-all duration-200 active:scale-[0.99]"
                  >
                    <span className="font-mono text-[26px] sm:text-[30px] font-extrabold text-[#1a1f6c] tracking-[0.12em] leading-none">
                      {result.code}
                    </span>
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.span
                          key="check"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"
                        >
                          <CheckIcon className="w-4 h-4 text-emerald-600" />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="copy"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex-shrink-0 text-gray-300 group-hover:text-[#1a1f6c]/50 transition-colors"
                        >
                          <ClipboardDocumentIcon className="w-6 h-6" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                  <motion.p
                    animate={{ color: copied ? "#10b981" : "#9ca3af" }}
                    className="text-[10px] text-center mt-2 font-medium"
                  >
                    {copied ? "✓ Copied to clipboard!" : "Tap the code to copy it"}
                  </motion.p>
                </div>

                {/* QR code section */}
                <div className="px-5 py-7 flex flex-col items-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-6">
                    Or scan this QR at the kiosk
                  </p>

                  {/* QR image with skeleton fallback */}
                  <div className="relative w-[240px] h-[240px]">
                    {!qrLoaded && (
                      <div className="absolute inset-0 bg-gray-100 rounded-2xl animate-pulse" />
                    )}
                    {qrSrc && (
                      <motion.img
                        src={qrSrc}
                        alt={`QR code for ${result.code}`}
                        onLoad={() => setQrLoaded(true)}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={qrLoaded ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="w-[240px] h-[240px] rounded-2xl border border-gray-100 shadow-sm"
                      />
                    )}
                  </div>

                  <p className="text-[11px] text-gray-400 text-center mt-5 max-w-[210px] leading-relaxed">
                    Point your kiosk scanner at this QR, or type the code above manually.
                  </p>
                </div>
              </motion.div>

              {/* ── Instructions card ── */}
              <motion.div
                variants={fadeUp}
                className="bg-[#1a1f6c] rounded-2xl px-5 py-5"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-4">
                  How to Redeem
                </p>
                {[
                  { icon: "📍", text: "Go to any Kinitas Car Wash kiosk" },
                  { icon: "⌨️", text: "Enter the code above or show the QR" },
                  { icon: "✅", text: "The kiosk will apply your reward automatically" },
                  { icon: "🚗", text: "Enjoy your free wash!" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.07, duration: 0.3 }}
                    className="flex items-start gap-3 mb-3 last:mb-0"
                  >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[12px]">
                      {i + 1}
                    </span>
                    <span className="text-[13px] text-white/70 leading-relaxed">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>

              {/* ── Expires note ── */}
              <motion.div
                variants={fadeUp}
                className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3"
              >
                <span className="text-[14px]">⏰</span>
                <p className="text-[11px] text-amber-700 font-medium">
                  This code is single-use. Screenshot or copy it before closing this page.
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="flex justify-center pb-2">
                <img src="/kinitas-logo.svg" alt="Kinitas" className="h-5 w-auto opacity-20" />
              </motion.div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};
